"use client";

import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { BrandMark } from "@/components/BrandMark";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Button, Input } from "@/components/ui";
import {
  checkTokenLink,
  peekTokenFromLocation,
  TOKEN_LINK_INVALID_COPY,
  type TokenCheckResult,
} from "@/lib/token-link";

type TokenLinkGateProps = {
  checkPath: string;
  stripPath: string;
  newLinkHref: string;
  newLinkLabel: string;
  children: (token: string) => ReactNode;
};

export function TokenLinkGate({
  checkPath,
  stripPath,
  newLinkHref,
  newLinkLabel,
  children,
}: TokenLinkGateProps) {
  const [token] = useState(peekTokenFromLocation);
  const [status, setStatus] = useState<TokenCheckResult | "checking">("checking");

  useEffect(() => {
    window.history.replaceState(window.history.state, "", stripPath);
    let cancelled = false;
    void checkTokenLink(checkPath, token).then((result) => {
      if (!cancelled) setStatus(result);
    });
    return () => {
      cancelled = true;
    };
  }, [checkPath, stripPath, token]);

  if (status === "checking") {
    return (
      <p role="status" className="text-sm" style={{ color: "var(--muted)" }}>
        Verificando link…
      </p>
    );
  }
  if (status === "limited") {
    return (
      <p role="alert" className="notice error">
        Muitas tentativas. Tente novamente em instantes.
      </p>
    );
  }
  if (status === "error") {
    return (
      <p role="alert" className="notice error">
        Não foi possível verificar o link. Tente novamente em instantes.
      </p>
    );
  }
  if (status === "invalid") {
    return (
      <div className="flex flex-col gap-5">
        <p role="alert" className="notice error">
          {TOKEN_LINK_INVALID_COPY}
        </p>
        <Link href={newLinkHref} className="ui-button primary w-full text-center">
          {newLinkLabel}
        </Link>
      </div>
    );
  }
  return <>{children(token)}</>;
}

export function TokenLinkLayout({
  title,
  heroTitle,
  heroBody,
  description = "Use uma senha de 12 a 128 caracteres. A verificação em duas etapas permanece ativa.",
  children,
}: {
  title: string;
  heroTitle: string;
  heroBody: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <main className="login-shell grid lg:grid-cols-[minmax(280px,42%)_1fr]">
      <aside className="login-hero relative hidden overflow-hidden px-10 py-12 text-white lg:flex lg:flex-col lg:justify-between">
        <BrandMark invert />
        <div className="relative z-10 max-w-md space-y-4">
          <p className="text-xs font-semibold tracking-[0.18em] text-[#36A9E0] uppercase">Conta local</p>
          <h1 className="text-4xl font-bold tracking-tight text-white">{heroTitle}</h1>
          <p className="text-base leading-relaxed text-white/80">{heroBody}</p>
        </div>
        <p className="relative z-10 text-xs text-white/55">Grupo ASV · Portal de serviços</p>
      </aside>
      <section className="relative flex min-h-screen flex-col px-4 py-6 sm:px-8 sm:py-8 lg:px-12 lg:py-10">
        <div className="mb-6 flex items-start justify-between gap-4 lg:mb-8">
          <div className="lg:hidden">
            <BrandMark />
          </div>
          <div className="ml-auto">
            <ThemeToggle />
          </div>
        </div>
        <div className="mx-auto flex w-full max-w-md flex-1 flex-col justify-center gap-8">
          <header className="space-y-2">
            <p className="text-xs font-semibold tracking-[0.16em] text-[var(--muted)] uppercase">Portal ASV</p>
            <h2 className="text-2xl font-semibold tracking-tight sm:text-3xl">{title}</h2>
            <p className="text-sm leading-relaxed" style={{ color: "var(--muted)" }}>
              {description}
            </p>
          </header>
          <div className="login-card rounded-xl p-6 sm:p-8">{children}</div>
        </div>
      </section>
    </main>
  );
}

export function PasswordTokenForm({
  token,
  consumePath,
  submitLabel,
}: {
  token: string;
  consumePath: string;
  submitLabel: string;
}) {
  const router = useRouter();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError("");
    if (password.length < 12 || password.length > 128) {
      setError("A senha deve ter entre 12 e 128 caracteres.");
      return;
    }
    if (password !== confirm) {
      setError("As senhas não coincidem.");
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(consumePath, {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json", accept: "application/json" },
        body: JSON.stringify({ token, new_password: password }),
      });
      if (!response.ok) {
        const body = (await response.json().catch(() => null)) as { message?: string } | null;
        throw new Error(body?.message ?? "Não foi possível definir a senha.");
      }
      router.replace("/entrar?senha=redefinida");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Não foi possível definir a senha.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <form method="post" className="flex flex-col gap-5" onSubmit={submit}>
      <Input
        label="Nova senha"
        id="recovery-password"
        type="password"
        autoComplete="new-password"
        required
        minLength={12}
        maxLength={128}
        value={password}
        onChange={(event) => setPassword(event.target.value)}
      />
      <Input
        label="Confirmar nova senha"
        id="recovery-confirm"
        type="password"
        autoComplete="new-password"
        required
        minLength={12}
        maxLength={128}
        value={confirm}
        onChange={(event) => setConfirm(event.target.value)}
      />
      {error ? (
        <p role="alert" className="notice error">
          {error}
        </p>
      ) : null}
      <Button type="submit" variant="primary" loading={loading} className="w-full">
        {submitLabel}
      </Button>
      <Link href="/entrar" className="text-center text-sm underline">
        Voltar para entrar
      </Link>
    </form>
  );
}
